import React, { Fragment, useState, useEffect } from "react";
import { closeModal, openModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faTrash, faEdit } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";
import { toast } from "react-toastify";

export default function ModalBatchRefunds(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { current_staff, permissions } = useSelector((state) => state.auth);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [paystackBalance, setPaystackBalance] = useState(0);
  const [peachBalance, setPeachBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const canProcess = permissions?.includes('receive_refund_otp');
  const [selectedGroup, setSelectedGroup] = useState(canProcess ? "Sarel" : "CI Refund");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (group = selectedGroup) => {
    try {
      setLoading(true);

      // Fetch pending payments
      const params = { 
        staff_id: current_staff.id,
        refund_group: group
      };
      
      const paymentsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}payments/batch/pending`,
        { params },
      );

      if (paymentsResponse.data.success) {
        setPendingPayments(paymentsResponse.data.payments);
        setTotal(paymentsResponse.data.total);
      }

      // Fetch Paystack balance
      const paystackBalanceResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}payments/batch/paystack-balance`,
      );

      if (paystackBalanceResponse.data.success) {
        setPaystackBalance(paystackBalanceResponse.data.balance || 0);
      }

      // Fetch Peach balance
      const peachBalanceResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}payments/batch/peach-balance`,
      );

      if (peachBalanceResponse.data.success) {
        setPeachBalance(peachBalanceResponse.data.balance || 0);
      }
    } catch (error) {
      console.error("Error fetching batch data:", error);
      toast.error("Failed to load batch data");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (paymentId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this refund from the queue?",
      )
    ) {
      return;
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}payments/batch/pending/${paymentId}`,
      );

      if (response.data.success) {
        toast.success("Refund removed from queue");
        fetchData(); // Refresh data
      } else {
        toast.error(response.data.message || "Failed to delete refund");
      }
    } catch (error) {
      console.error("Error deleting refund:", error);
      toast.error(error.response?.data?.message || "Failed to delete refund");
    }
  };

  const handleEdit = (payment) => {
    dispatch(
      openModal({
        modal_type: "ModalEditRefund",
        modal_props: {
          payment: payment,
          onSuccess: fetchData,
        },
      }),
    );
  };

  const handleGroupChange = (e) => {
    const group = e.target.value;
    setSelectedGroup(group);
    fetchData(group);
  };

  const handleProcessBatch = async () => {
    if (pendingPayments.length === 0) {
      toast.error("No pending refunds to process");
      return;
    }

    const combinedBalance = paystackBalance + peachBalance;

    if (combinedBalance < total) {
      toast.error(
        `Insufficient balance. Combined: R ${combinedBalance.toFixed(2)}, Required: R ${total.toFixed(2)}`,
      );
      return;
    }

    // Check if ALL payments in batch have staging enabled
    const hasStaging =
      pendingPayments.length > 0 &&
      pendingPayments.every((payment) => payment.is_test);

    // Open OTP modal
    dispatch(
      openModal({
        modal_type: "ModalBatchOTP",
        modal_props: {
          total: total,
          count: pendingPayments.length,
          hasStaging: hasStaging,
          refund_group: selectedGroup,
          onSuccess: () => {
            fetchData();
            toast.success("Batch processing started");
          },
        },
      }),
    );
  };

  const combinedBalance = paystackBalance + peachBalance;
  const remainingBalance = combinedBalance - total;
  const balanceColor =
    remainingBalance >= 0 ? "text-green-600" : "text-red-600";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div className="pr-8 w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900 mb-4"
                    >
                      Batch Refund Queue
                    </Dialog.Title>

                    {/* Group Filter */}
                    <div className="mb-4">
                      <label
                        htmlFor="group-filter"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Filter by Group
                      </label>
                      <div className="flex gap-2">
                        <select
                          id="group-filter"
                          value={selectedGroup}
                          onChange={handleGroupChange}
                          className="flex-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          {canProcess && <option value="Sarel">Sarel</option>}
                          <option value="CI Refund">CI Refund</option>
                          <option value="Cash Back">Cash Back</option>
                          <option value="Cash Back 2025">Cash Back 2025</option>
                        </select>
                        <button
                          onClick={() => fetchData()}
                          disabled={loading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          Refresh
                        </button>
                      </div>
                    </div>

                    {/* Tables */}
                    {loading ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        <p className="mt-2 text-sm text-gray-500">Loading...</p>
                      </div>
                    ) : pendingPayments.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">
                          No pending refunds in queue
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Paystack Table */}
                        {pendingPayments.filter(
                          (p) => p.payment_provider !== "peach",
                        ).length > 0 && (
                          <div className="mb-6">
                            <div className="bg-blue-50 p-3 rounded-t-lg border-b-2 border-blue-200">
                              <div className="flex justify-between items-center">
                                <h3 className="text-sm font-semibold text-blue-900">
                                  Paystack Payouts
                                </h3>
                                <div className="text-sm">
                                  <span className="font-medium">Balance:</span>
                                  <span className="ml-2 text-blue-600">
                                    R {paystackBalance.toFixed(2)}
                                  </span>
                                  <span className="mx-2">|</span>
                                  <span className="font-medium">Total:</span>
                                  <span className="ml-2 text-orange-600">
                                    R{" "}
                                    {pendingPayments
                                      .filter(
                                        (p) => p.payment_provider !== "peach",
                                      )
                                      .reduce((sum, p) => sum + p.amount, 0)
                                      .toFixed(2)}
                                  </span>
                                  <span className="mx-2">|</span>
                                  <span className="font-medium">
                                    Remaining:
                                  </span>
                                  <span
                                    className={`ml-2 ${paystackBalance - pendingPayments.filter((p) => p.payment_provider !== "peach").reduce((sum, p) => sum + p.amount, 0) >= 0 ? "text-green-600" : "text-red-600"}`}
                                  >
                                    R{" "}
                                    {(
                                      paystackBalance -
                                      pendingPayments
                                        .filter(
                                          (p) => p.payment_provider !== "peach",
                                        )
                                        .reduce((sum, p) => sum + p.amount, 0)
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Type
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Ref
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      TC Name
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Amount
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Reason
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Queued
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      By
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {pendingPayments
                                    .filter(
                                      (p) => p.payment_provider !== "peach",
                                    )
                                    .map((payment) => (
                                      <tr key={payment.id}>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                                          {payment.is_test ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                              TEST
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                              LIVE
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {payment.booking_reference}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {payment.tc_name}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          R {payment.amount.toFixed(2)}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate">
                                          {payment.refund_reason}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {payment.queued_at}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {payment.queued_by}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                                          <button
                                            onClick={() => handleEdit(payment)}
                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                            title="Edit"
                                          >
                                            <FontAwesomeIcon icon={faEdit} />
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleDelete(payment.id)
                                            }
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete"
                                          >
                                            <FontAwesomeIcon icon={faTrash} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Peach Table */}
                        {pendingPayments.filter(
                          (p) => p.payment_provider === "peach",
                        ).length > 0 && (
                          <div className="mb-6">
                            <div className="bg-purple-50 p-3 rounded-t-lg border-b-2 border-purple-200">
                              <div className="flex justify-between items-center">
                                <h3 className="text-sm font-semibold text-purple-900">
                                  Peach Payouts
                                </h3>
                                <div className="text-sm">
                                  <span className="font-medium">Balance:</span>
                                  <span className="ml-2 text-purple-600">
                                    R {peachBalance.toFixed(2)}
                                  </span>
                                  <span className="mx-2">|</span>
                                  <span className="font-medium">Total:</span>
                                  <span className="ml-2 text-orange-600">
                                    R{" "}
                                    {pendingPayments
                                      .filter(
                                        (p) => p.payment_provider === "peach",
                                      )
                                      .reduce((sum, p) => sum + p.amount, 0)
                                      .toFixed(2)}
                                  </span>
                                  <span className="mx-2">|</span>
                                  <span className="font-medium">
                                    Remaining:
                                  </span>
                                  <span
                                    className={`ml-2 ${peachBalance - pendingPayments.filter((p) => p.payment_provider === "peach").reduce((sum, p) => sum + p.amount, 0) >= 0 ? "text-green-600" : "text-red-600"}`}
                                  >
                                    R{" "}
                                    {(
                                      peachBalance -
                                      pendingPayments
                                        .filter(
                                          (p) => p.payment_provider === "peach",
                                        )
                                        .reduce((sum, p) => sum + p.amount, 0)
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Type
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Ref
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      TC Name
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Amount
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Reason
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Queued
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      By
                                    </th>
                                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {pendingPayments
                                    .filter(
                                      (p) => p.payment_provider === "peach",
                                    )
                                    .map((payment) => (
                                      <tr key={payment.id}>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm">
                                          {payment.is_test ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                              TEST
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                              LIVE
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {payment.booking_reference}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          {payment.tc_name}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                                          R {payment.amount.toFixed(2)}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-900 max-w-xs truncate">
                                          {payment.refund_reason}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {payment.queued_at}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                                          {payment.queued_by}
                                        </td>
                                        <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium">
                                          <button
                                            onClick={() => handleEdit(payment)}
                                            className="text-blue-600 hover:text-blue-900 mr-3"
                                            title="Edit"
                                          >
                                            <FontAwesomeIcon icon={faEdit} />
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleDelete(payment.id)
                                            }
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete"
                                          >
                                            <FontAwesomeIcon icon={faTrash} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Footer */}
                    <div className="mt-6 flex justify-between items-center">
                      <div className="text-lg font-medium">
                        Total: R {total.toFixed(2)}
                      </div>
                      {canProcess && pendingPayments.length > 0 && (
                        <button
                          onClick={handleProcessBatch}
                          disabled={processing || remainingBalance < 0}
                          className={`inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                            remainingBalance < 0
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                          }`}
                        >
                          {processing ? "Processing..." : "Process Batch"}
                        </button>
                      )}
                      {!canProcess && (
                        <p className="text-sm text-gray-500 italic">
                          Only authorized staff can process batch refunds
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
